import { decodeJWT } from "../library";
import authorizationMap from "../authorization/ScreenAuthority.json";

interface ScreenAuthority {
  name: string;
  view: string[];
  insert: string[];
  update: string[];
  delete: string[];
}

interface DecodedToken {
  // The backend mints `roles` as a COMMA-JOINED STRING (catchup-security
  // JwtUtilization → Collectors.joining(",")); older/test tokens may be an array.
  roles: string | string[];
  [key: string]: unknown;
}

const findAuthority = (screenName: string): ScreenAuthority | undefined =>
  (authorizationMap as ScreenAuthority[]).find(
    (authorization) => authorization.name === screenName,
  );

// An area renamed mid-flight is still the same permission. A token is a signed snapshot, so one
// minted before the user_authorities rows were renamed carries the old name for up to its lifetime
// (4h access, 1 week refresh) and must still open the same screens. The backend's @PreAuthorize
// accepts either name for the same reason; this is that rule, client-side.
const LEGACY_AUTHORITY_NAMES: Record<string, string> = {
  BRANCH_ASSIGNMENT_READ: "CORPORATE_ASSIGNMENT_READ",
  BRANCH_ASSIGNMENT_MANAGE: "CORPORATE_ASSIGNMENT_MANAGE",
};

const checkAuthorityList = (
  required: string[],
  grantedClaim: string | string[] | undefined,
): boolean => {
  // Normalize the `roles` claim to a string[] — the backend mints it as a comma-joined
  // string, so exact array membership (not fragile String.includes substring matching)
  // decides the grant. Mirrors catchup-admin-web's ScreenUtilization.
  const granted = Array.isArray(grantedClaim)
    ? grantedClaim
    : (grantedClaim || "").split(",");
  if (required.length === 0 || granted.length === 0) return false;
  for (const authority of required) {
    const legacy = LEGACY_AUTHORITY_NAMES[authority];
    if (!granted.includes(authority) && !(legacy && granted.includes(legacy))) {
      return false;
    }
  }
  return true;
};

export const checkViewPermissionGranted = (screenName: string): boolean => {
  const accessToken = localStorage.getItem("accessToken");
  return checkViewPermissionGrantedByToken(screenName, accessToken);
};

export const checkInsertPermissionGranted = (screenName: string): boolean => {
  const accessToken = localStorage.getItem("accessToken");
  return checkInsertPermissionGrantedByToken(screenName, accessToken);
};

export const checkDeletePermissionGranted = (screenName: string): boolean => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) return false;
  const tokenJSON = decodeJWT(accessToken) as DecodedToken;
  const authorization = findAuthority(screenName);
  if (!authorization) return false;
  return checkAuthorityList(authorization.delete, tokenJSON.roles);
};

export const checkUpdatePermissionGranted = (screenName: string): boolean => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) return false;
  const tokenJSON = decodeJWT(accessToken) as DecodedToken;
  const authorization = findAuthority(screenName);
  if (!authorization) return false;
  return checkAuthorityList(authorization.update, tokenJSON.roles);
};

export const checkViewPermissionGrantedByToken = (
  screenName: string,
  accessToken: string | null,
): boolean => {
  if (!accessToken) return false;
  const tokenJSON = decodeJWT(accessToken) as DecodedToken;
  const authorization = findAuthority(screenName);
  if (!authorization) return false;
  return checkAuthorityList(authorization.view, tokenJSON.roles);
};

export const checkInsertPermissionGrantedByToken = (
  screenName: string,
  accessToken: string | null,
): boolean => {
  if (!accessToken) return false;
  const tokenJSON = decodeJWT(accessToken) as DecodedToken;
  const authorization = findAuthority(screenName);
  if (!authorization) return false;
  return checkAuthorityList(authorization.insert, tokenJSON.roles);
};
